import { Box, Alert, CircularProgress } from '@mui/material';
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { VenueDisplayCard } from '../VenueDisplayCard';
import { useVenue } from '../../../../venues/hooks/useVenues';

interface VenueSelectionSectionProps {
  initialVenueId?: string;
  onComplete: (data: unknown, summary: string) => void;
  registerContinue?: (callback: () => void) => void;
}

export function VenueSelectionSection({
  initialVenueId,
  onComplete,
  registerContinue,
}: VenueSelectionSectionProps) {
  // Fetch venue details if venueId provided
  const { data: venue, isLoading, error } = useVenue(initialVenueId || '');

  const handleContinue = () => {
    if (!initialVenueId) {
      return;
    }

    const data = { venueId: initialVenueId };
    const summary = venue?.name || 'Venue selected';
    onComplete(data, summary);
  };

  useEffect(() => {
    if (registerContinue) {
      registerContinue(handleContinue);
    }
  }, [registerContinue, handleContinue]);

  if (!initialVenueId) {
    return (
      <Alert severity="error">No venue selected. Please start from a venue detail page.</Alert>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !venue) {
    return <Alert severity="error">Failed to load venue details. Please try again.</Alert>;
  }

  return (
    <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <VenueDisplayCard
          venue={{
            id: venue.id,
            name: venue.name,
            coverImage: venue.coverImage || undefined,
            address: venue.address,
            basePrice: venue.basePrice,
            currency: venue.currency,
          }}
        />
      </Box>
    </motion.div>
  );
}
